﻿import {
	hasFlag,
	KeysetQuery,
	KeysetQueryResult,
	KeysetQueryState,
	mergeObjects,
	Paths,
	readNestedProperty
} from "@2d-front-monorepo/core";
import {JSX, ReactNode, RefObject, useMemo} from "react";
import {ButtonType} from "./ButtonType";
import {DEFAULT_OPTIONS} from "./DEFAULT_OPTIONS";
import {PageAriaLabels} from "./PageAriaLabels";

interface Button {
	disabled?: boolean
	first?: boolean;
	last?: boolean;
	before?: any;
	after?: any;
	type?: ButtonType
}

function getItemClass(button: Button): string {
	const root = "pagination__item";
	let type;
	switch (button.type) {
		case ButtonType.First: {
			type = " pagination__item--first";
			break;
		}
		case ButtonType.Previous: {
			type = " pagination__item--prev";
			break;
		}
		case ButtonType.Next: {
			type = " pagination__item--next";
			break;
		}
		case ButtonType.Last: {
			type = " pagination__item--last";
			break;
		}
		default: {
			type = "";
			break;
		}
	}

	return button.disabled ? `${root}${type} pagination__item--disabled` : `${root}${type}`;
}

function getButtons<TKey, TItem extends object>(items: KeysetPagerProps<TKey, TItem>): Button[] {
	const {state, data} = items.items;
	const hasPrevious = hasFlag(state, KeysetQueryState.HasPrevious);
	const hasNext = hasFlag(state, KeysetQueryState.HasNext);
	return [
		{
			disabled: hasFlag(state, KeysetQueryState.IsFirst) || !hasPrevious,
			first: true,
			type: ButtonType.First
		},
		{
			disabled: !hasPrevious,
			before: readNestedProperty(data[0], items.keyField),
			type: ButtonType.Previous
		},
		{
			disabled: !hasNext,
			after: readNestedProperty(data[data.length - 1], items.keyField),
			type: ButtonType.Next
		},
		{
			disabled: hasFlag(state, KeysetQueryState.IsLast) || !hasNext,
			last: true,
			type: ButtonType.Last
		}
	];
}

interface PagerLinkProps<TKey> {
	button: Button,
	icon?: string,
	size?: number,
	ariaLabel: string,
	onPageChange: (query: KeysetQuery<TKey>) => void
}

function PagerLink<TKey>(props: PagerLinkProps<TKey>): JSX.Element {
	const {button, icon, ariaLabel, onPageChange, size} = props;
	if (button.disabled) {
		return <span data-icon={icon}></span>
	}

	return <button type={"button"}
	               className="pagination__link"
	               aria-label={ariaLabel}
	               onClick={() => onPageChange({
		               first: button.first,
		               before: button.before,
		               after: button.after,
		               last: button.last,
		               size
	               })}
	               data-icon={icon}>
	</button>;
}

interface PagerButtonProps<TKey> {
	button: Button,
	options: KeysetPagerOptions,
	onPageChange: (query: KeysetQuery<TKey>) => void,
	size?: number
}

function PagerButton<TKey>(props: PagerButtonProps<TKey>): JSX.Element | undefined {
	const {button, options, onPageChange, size} = props;
	const {first, previous, next, last} = options.ariaLabels!;

	switch (button.type) {
		case ButtonType.First: {
			return <PagerLink button={button} icon={"pager-icon-first"} ariaLabel={first} onPageChange={onPageChange}
			                  size={size}/>
		}

		case ButtonType.Previous: {
			return <PagerLink button={button} icon={"pager-icon-previous"} ariaLabel={previous}
			                  onPageChange={onPageChange} size={size}/>
		}

		case ButtonType.Next: {
			return <PagerLink button={button} icon={"pager-icon-next"} ariaLabel={next} onPageChange={onPageChange}
			                  size={size}/>
		}

		case ButtonType.Last: {
			return <PagerLink button={button} icon={"pager-icon-last"} ariaLabel={last} onPageChange={onPageChange}
			                  size={size}/>
		}
	}

	return undefined;
}

export interface KeysetPagerOptions {
	ariaLabels?: PageAriaLabels
}

export interface KeysetPagerProps<TKey, TItem extends object> {
	keyField: Paths<TItem>;
	items: KeysetQueryResult<TItem>;
	onPageChange: (query: KeysetQuery<TKey>) => void;
	options?: KeysetPagerOptions;
	children?: ReactNode;
	ref?: RefObject<HTMLDivElement | null>
}

export default function KeysetPager<TKey, TItem extends object>(props: KeysetPagerProps<TKey, TItem>): JSX.Element {
	const options = useMemo(() => mergeObjects(DEFAULT_OPTIONS, props.options)!, [props.options]);
	const buttons = useMemo(() => getButtons(props), [options, props.items, props.keyField]);

	return <div className="pagination" ref={props.ref}>
		<nav aria-label="pagination">
			<ol className="pagination__list">
				{buttons.map((button) =>
					<li key={button.type} className={getItemClass(button)}>
						<PagerButton button={button} options={options} onPageChange={props.onPageChange}
						             size={props.items.pageSize}/>
					</li>
				)}
			</ol>
		</nav>
		{props.children}
	</div>;
}
